import {
  CacheAndStaleTimeForUnlikelyChanged,
  useMutationWithToastError,
  useQueryWithToastError,
} from './queryUtils';
import {
  addNewSite,
  deleteSite,
  getAllSites,
  getMergedSiteSettings,
  getSiteById,
  getSiteByName,
  updateSite,
} from '../services/sites';
import {
  SiteData,
  SiteFormValues,
  SitesFilters,
  UpdateSiteRequest,
} from '../types/sites';
import { useQueryClient } from 'react-query';
import {
  UseMutationOptions,
  UseQueryOptions,
} from 'react-query/types/react/types';
import { AxiosResponse } from 'axios';

const SITES_QUERY_KEY = 'sites';

const MERGED_SITES_SETTINGS_QUERY_KEY = [SITES_QUERY_KEY, 'mergedSettings'];

export const useQuerySites = (filters?: SitesFilters) => {
  return useQueryWithToastError(
    [SITES_QUERY_KEY, filters],
    () => getAllSites(filters),
    {
      ...CacheAndStaleTimeForUnlikelyChanged,
      select: (data) => data.data,
    },
  );
};

export const useQuerySitesByName = (
  namePart: string | undefined,
  minLength: number,
) => {
  return useQueryWithToastError(
    [SITES_QUERY_KEY, 'byName', namePart],
    () => getSiteByName(namePart!),
    {
      enabled: !!namePart && namePart.trim().length >= minLength,
    },
  );
};

export const useQuerySite = (
  id?: string,
  options?: UseQueryOptions<
    AxiosResponse<SiteData>,
    any,
    AxiosResponse<SiteData>
  >,
) => {
  return useQueryWithToastError(
    [SITES_QUERY_KEY, { id: id }],
    () => getSiteById(id!),
    {
      ...CacheAndStaleTimeForUnlikelyChanged,
      ...options,
    },
  );
};

export const useMergedSiteSettings = (siteIds?: string[]) => {
  return useQueryWithToastError(
    [...MERGED_SITES_SETTINGS_QUERY_KEY, { siteIds }],
    () => getMergedSiteSettings(siteIds!!),
    {
      ...CacheAndStaleTimeForUnlikelyChanged,
      select: (siteResponse) => siteResponse.data,
      enabled: Boolean(siteIds && siteIds.length > 0),
    },
  );
};

export const useMutateCreateSite = (
  options?: UseMutationOptions<any, any, SiteFormValues>,
) => {
  const client = useQueryClient();
  return useMutationWithToastError<SiteFormValues>(
    [SITES_QUERY_KEY],
    (siteUpdate) => addNewSite(siteUpdate),
    {
      onSettled: () => client.invalidateQueries([SITES_QUERY_KEY]),
      ...options,
    },
  );
};

export const useMutateUpdateSite = (
  options?: UseMutationOptions<any, any, UpdateSiteRequest>,
) => {
  const client = useQueryClient();
  return useMutationWithToastError<UpdateSiteRequest>(
    [SITES_QUERY_KEY],
    (siteUpdate) => updateSite(siteUpdate),
    {
      onSettled: () => client.invalidateQueries([SITES_QUERY_KEY]),
      ...options,
    },
  );
};

export const useMutateDeleteSite = (
  options?: UseMutationOptions<any, any, string>,
) => {
  const client = useQueryClient();
  return useMutationWithToastError<string>(
    [SITES_QUERY_KEY],
    (id) => deleteSite(id),
    {
      onSettled: () => client.invalidateQueries([SITES_QUERY_KEY]),
      ...options,
    },
  );
};
